import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, NgZone, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { FSchddService } from './f-schdd.service';
import { ReportableTransactionComponent } from '../reportable-transaction/reportable-transaction.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f-schdd',
    templateUrl: './f-schdd.component.html',
    styleUrls: ['./f-schdd.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FSchddComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // Data loading optimization
  private dataLoadingStates = {
    entityData: false,
    formData: false
  };

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS
  rowData: any = {};

  // Component properties migrated from FScheduleDControllerModal
  modalTitle: string = '';
  scheduleDHeader: any = {};
  scheduleDList: any[] = [];
  scheduleDOriginalData: any[] = [];
  scheduleDFormData: any[] = [];
  scheduleDFinalData: any[] = [];
  unAllocatedAmt: number = 0;
  codeAdj: string[] = ['B', 'T', 'N', 'H', 'D', 'Q', 'X', 'R', 'W', 'L', 'E', 'S', 'C', 'M', 'O', 'Z', 'Y'];

  // Checkbox on 8949 dropdown options per line
  checkbox8949OptionsMap: { [key: string]: string[] } = {
    '1b': ['A', 'G'],
    '2': ['B', 'H'],
    '3': ['C', 'I'],
    '8b': ['D', 'J'],
    '9': ['E', 'K'],
    '10': ['F', 'L']
  };

  isValidScenario: boolean = false;
  isExamScenario: boolean = false;

  get isLocked(): boolean {
    const gp: any = this.globalService?.globalParams ?? this.globalService ?? {};
    return String(gp?.is_locked ?? '').toUpperCase() === 'Y';
  }
  categoryCode: any = {};
  logged_in_user: string = '';
  activeTab: number = 1;

  // Tab configuration
  tabs = [
    { id: 1, name: "Form Entry" }
  ];

  // Flag to prevent multiple concurrent loads during initialization
  private dataLoaded: boolean = false;

  // Reference to the reportable transaction dialog
  private reportableTransactionDialogRef: MatDialogRef<ReportableTransactionComponent> | null = null;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private fSchddService: FSchddService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit() {
    this.applyWorkspaceContext();
    // IMMEDIATE data check - don't wait for async operations
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.setupEntityData();  // Show entity info immediately
      this.showFormLoader = false;  // Hide loader immediately if data exists
      this.init();  // Start data processing
    } else {
      // Only show loader if no initial data
      this.showFormLoader = true;
    }
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.dataLoaded = false;
        this.applyWorkspaceContext();
        if (this.rowData && Object.keys(this.rowData).length > 0) {
          this.setupEntityData();
          this.showFormLoader = false;
          this.init();
        }
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.dataLoaded = false;
        this.applyWorkspaceContext();
        if (this.rowData && Object.keys(this.rowData).length > 0) {
          this.setupEntityData();
          this.showFormLoader = false;
          this.init();
        }
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && this.GENERAL_CONFIG && !this.dataLoaded) {
        this.dataLoaded = true; // Set flag to true to prevent re-entry from multiple @Input setters
        this.setupEntityData();  // ALWAYS call this first
        this.loadFormData();
      }
    } catch (e) {
      console.error('Error in F-Schdd init:', e);
      this.showFormLoader = false;  // Always hide loader on error
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.CDR_NO || this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];

    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  checkAllDataLoaded() {
    const allLoaded = Object.values(this.dataLoadingStates).every(state => state);
    if (allLoaded) {
      this.showFormLoader = false;
      this.cdr.detectChanges();  // Trigger change detection
    }
  }

  loadFormData() {
    if (!this.GENERAL_CONFIG || !this.rowData) {
      return;
    }

    this.logged_in_user = this.appService.USER_SETTINGS?.user?.sso_id || '';
    if (!this.logged_in_user) {
      console.error('User SSO ID not available');
      this.showFormLoader = false;
      return;
    }

    // Reset state before loading new data to ensure clean reload (e.g. after save & reopen)
    this.showFormLoader = true;
    this.scheduleDFormData = [];
    this.scheduleDOriginalData = [];
    this.scheduleDFinalData = [];
    this.isValidScenario = false;
    this.dataLoadingStates.formData = false;
    this.cdr.detectChanges();

    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;

    this.fSchddService.fetchSchddDetails(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      scenario,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.callSuccess === "1") {
          this.processFormData(response);
          this.dataLoadingStates.formData = true;
          this.checkAllDataLoaded();
        } else {
          this._snackBar.open(response?.errorMessage || 'Error loading form data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.showFormLoader = false;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading F-Schdd form data:', error);
        this._snackBar.open('Error occurred while loading form data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.cdr.markForCheck();
      }
    );

    // Load category code dropdown
    this.loadCategoryCodeDropdown();
  }

  loadCategoryCodeDropdown() {
    if (!this.GENERAL_CONFIG || !this.rowData || !this.logged_in_user) {
      return;
    }

    this.fSchddService.fetchCategoryCodeDropdownValues(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.callSuccess === "1" && response.jsonObject) {
          response.jsonObject.forEach((element: any) => {
            this.categoryCode[element.CODE_NAME] = element.CODE_DESC;
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading category code dropdown:', error);
      }
    );
  }

  processFormData(data: any) {
    try {
      if (!data.jsonObject || data.jsonObject.length === 0) {
        return;
      }

      this.scheduleDHeader = data.jsonObject[0];
      this.scheduleDList = data.jsonObject.slice(1);
      this.modalTitle = this.scheduleDHeader.ATTRIB_DESCRIPTION || 'Schedule D';

      // Check valid scenario
      for (let i = 0; i < this.scheduleDList.length; i++) {
        if (!this.isValidScenario && this.scheduleDList[i].T_SCENARIO_TYPE_CODE) {
          this.isValidScenario = this.checkEditScenario(
            this.scheduleDList[i].T_SCENARIO_TYPE_CODE,
            this.scheduleDList[i].T_SCENARIO,
            this.scheduleDList[i].ISSUE_KEY
          );
        }
      }

      // Group by LINE_ORDER
      const groupedByLineOrder = this.scheduleDList.reduce((acc: any, item: any) => {
        const lineOrder = item.LINE_ORDER;
        if (!acc[lineOrder]) {
          acc[lineOrder] = [];
        }
        acc[lineOrder].push(item);
        return acc;
      }, {});

      // Convert to array format
      this.scheduleDFormData = Object.keys(groupedByLineOrder).map((key: string) => {
        return {
          ROW_TYPE: groupedByLineOrder[key][0].ROW_TYPE,
          DELETE_ROWS: [],
          Line_data: groupedByLineOrder[key]
        };
      });

      // Process each row type
      for (let i = 0; i < this.scheduleDFormData.length; i++) {
        if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
          this.processCHRowType(this.scheduleDFormData[i]);
        } else if (this.scheduleDFormData[i].ROW_TYPE == 'DT') {
          this.processDTRowType(this.scheduleDFormData[i]);
        }
      }

      this.scheduleDOriginalData = _.cloneDeep(this.scheduleDFormData);
      this.calcFinalAmounts(); // Calculate TH row values on initial load
      this.getUnallocatedAmt();
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error processing form data:', e);
      this.showFormLoader = false;
    }
  }

  processCHRowType(row: any) {
    if (row.Line_data[0].OCCURRENCE != null) {
      // Group by OCCURRENCE
      const groupedByOccurrence = row.Line_data.reduce((acc: any, item: any) => {
        const occurrence = item.OCCURRENCE;
        if (!acc[occurrence]) {
          acc[occurrence] = [];
        }
        acc[occurrence].push(item);
        return acc;
      }, {});

      let processedData = Object.keys(groupedByOccurrence).map((key: string) => {
        const items = groupedByOccurrence[key];
        return {
          Line_Type: "DATA_ROW",
          Is_Multi: items[0].IS_MULTI,
          Line_no: items[0].LINE_NO,
          IS_EDITABLE: items[0].IS_EDITABLE,
          IS_EDITTED: "N",
          Trans_details_key: items[0].TRANS_DETAILS_KEY,
          Occurrence: items[0].OCCURRENCE,
          Line_data_occ: items
        };
      });

      // Insert comment rows
      // NOTE: Do NOT use shared references between comment row and data row in Angular 12.
      // AngularJS uses shared refs + dirty checking which works, but Angular 12's OnPush + [(ngModel)]
      // causes the shared object to corrupt data bindings. Instead, use a CLONE for the comment row.
      // The main save() function already merges the comment value back into the data row via:
      //   data[j].occ[last] = data[j+1].occ[0]
      const commentRows = _.cloneDeep(processedData);
      for (let x = 0; x < commentRows.length; x++) {
        const d = (2 * x) + 1;
        processedData.splice(d, 0, commentRows[x]);
        processedData[d].Line_Type = "DATA_COMMENTS";
        // Use cloned comment row's own last element (NOT a shared reference to the data row)
        processedData[d].Line_data_occ = [processedData[d].Line_data_occ[processedData[d].Line_data_occ.length - 1]];
      }

      // Add header row
      const headerRow = _.cloneDeep(processedData[0]);
      headerRow.Line_Type = "HEADER_ROW";
      headerRow.Occurrence = "";
      headerRow.Trans_details_key = "";
      headerRow.IS_EDITABLE = "Y";
      for (let j = 0; j < headerRow.Line_data_occ.length; j++) {
        headerRow.Line_data_occ[j].ATTRIB_VALUE = headerRow.Line_data_occ[j].ATTRIB_DESCRIPTION;
      }

      processedData.unshift(headerRow, {
        Line_Type: "SUB_ROW",
        Line_no: headerRow.Line_no,
        Line_data_occ: [],
        Is_Multi: headerRow.Is_Multi || 'N',
        IS_EDITABLE: 'N',
        IS_EDITTED: 'N',
        Trans_details_key: '',
        Occurrence: ''
      });
      row.Line_data = processedData;
    } else {
      // Group by LINE_NO
      const groupedByLineNo = row.Line_data.reduce((acc: any, item: any) => {
        const lineNo = item.LINE_NO;
        if (!acc[lineNo]) {
          acc[lineNo] = [];
        }
        acc[lineNo].push(item);
        return acc;
      }, {});

      const processedData = Object.keys(groupedByLineNo).map((key: string) => {
        const items = groupedByLineNo[key];
        const result = {
          Line_Type: "HEADER_ROW",
          Is_Multi: items[0].IS_MULTI,
          Line_no: items[0].LINE_NO,
          IS_EDITABLE: items[0].IS_EDITABLE,
          IS_EDITTED: "N",
          Trans_details_key: "",
          Occurrence: "",
          Line_data_occ: items
        };

        for (let j = 0; j < result.Line_data_occ.length; j++) {
          result.Line_data_occ[j].ATTRIB_VALUE = result.Line_data_occ[j].ATTRIB_DESCRIPTION;
        }

        return result;
      });

      processedData.push({
        Line_Type: "SUB_ROW",
        Line_no: processedData[0].Line_no,
        Line_data_occ: [],
        Is_Multi: processedData[0].Is_Multi || 'N',
        IS_EDITABLE: 'N',
        IS_EDITTED: 'N',
        Trans_details_key: '',
        Occurrence: ''
      });
      row.Line_data = processedData;
    }
  }

  processDTRowType(row: any) {
    for (let j = 0; j < row.Line_data.length; j++) {
      if (row.Line_data[j].IS_EDITABLE == 'Y' &&
          row.Line_data[j].EDIT_TYPE == 'currency' &&
          row.Line_data[j].DATA_TYPE == 'number') {
        row.Line_data[j].ATTRIB_VALUE = parseInt(row.Line_data[j].ATTRIB_VALUE);
      }
      if (row.Line_data[j].IS_EDITABLE == 'Y' &&
          row.Line_data[j].ATTRIB_SHORT_CODE == 'LINE_NO_12' &&
          row.Line_data[j].T_SCENARIO == this.globalService?.globalParams?.scenario &&
          this.globalService?.globalParams?.tax_year >= "2023" &&
          this.rowData.CONSOL_GROUP_KEY == "3") {
        row.Line_data[j].ATTRIB_VALUE = '11';
      }
    }
  }

  checkEditScenario(s_type_code: string, t_scenario: string, t_issue: string): boolean {
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    const glbl_issue_id = this.globalService?.globalParams?.issue_id;

    if (scenario == t_scenario &&
        ((s_type_code == 'EXAM' && glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  getUnallocatedAmt() {
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'TH') {
        if (this.scheduleDFormData[i].Line_data[1]?.DESCRIPTION == 'Unallocated Amount' &&
            this.scheduleDFormData[i].Line_data[1]?.DATA_TYPE == 'number' &&
            this.scheduleDFormData[i].Line_data[1]?.ATTRIB_VALUE != null &&
            this.scheduleDFormData[i].Line_data[1]?.ATTRIB_VALUE != undefined) {
          this.unAllocatedAmt = parseInt(this.scheduleDFormData[i].Line_data[1].ATTRIB_VALUE);
        }
      }
    }
  }

  save() {
    // Check if already saving (matching AngularJS pattern)
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    if (this.isLocked) {
      this._snackBar.open('Screen is locked — save disabled', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.scheduleDFinalData = _.cloneDeep(this.scheduleDFormData);

    // Check if data has changed
    if (_.isEqual(this.scheduleDFormData, this.scheduleDOriginalData)) {
      this.cancel();
      return;
    }

    // Update form data values before validation
    this.updateFormDataValues();

    // Validate form values
    const returnValue = this.validateFormValues();
    if (returnValue == 1) {
      const errorsByMessage: { [message: string]: string[] } = {};
      for (const row of this.scheduleDFormData || []) {
        if (row.ROW_TYPE !== 'CH') continue;
        for (const ld of row.Line_data || []) {
          for (const item of ld.Line_data_occ || []) {
            if (item && item.HAS_ERROR === 'Y' && item.ATTRIB_VALUE_ERROR) {
              const msg = item.ATTRIB_VALUE_ERROR;
              if (!errorsByMessage[msg]) {
                errorsByMessage[msg] = [];
              }
              if (errorsByMessage[msg].indexOf(ld.Line_no) === -1) {
                errorsByMessage[msg].push(ld.Line_no);
              }
            }
          }
        }
      }
      let combinedError: string | null = null;
      const messages = Object.keys(errorsByMessage);
      if (messages.length > 0) {
        const parts: string[] = [];
        for (const msg of messages) {
          const lines = errorsByMessage[msg];
          if (lines.length > 1) {
            parts.push(`Lines ${lines.join(', ')}: ${msg}`);
          } else {
            parts.push(`Line ${lines[0]}: ${msg}`);
          }
        }
        combinedError = parts.join(' | ');
      }
      this._snackBar.open(
        combinedError || 'Please correct the highlighted fields before saving.',
        'Close',
        { duration: 5000, panelClass: ['error-snackbar'] }
      );
      return;
    }

    // Check unallocated amount
    if (this.unAllocatedAmt != 0) {
      if (!confirm("Allocated Amount is not fully adjusted among Capital Gain or (Loss). Confirm to save the changes.")) {
        return;
      }
    }

    // Set loading states only after all validations pass
    this.isSaveClicked = true;
    this.crudLoading = true;

    const finalData: any[] = [];

    // Process final data
    for (let x = 0; x < this.scheduleDFinalData.length; x++) {
      if (this.scheduleDFinalData[x].ROW_TYPE == 'CH') {
        this.scheduleDFinalData[x].Line_data.splice(0, 2);
        if (this.scheduleDFinalData[x].Line_data.length != null &&
            this.scheduleDFinalData[x].Line_data.length != undefined) {
          for (let j = 0; j < this.scheduleDFinalData[x].Line_data.length; j++) {
            if (this.scheduleDFinalData[x].Line_data[j + 1]) {
              this.scheduleDFinalData[x].Line_data[j].Line_data_occ[
                this.scheduleDFinalData[x].Line_data[j].Line_data_occ.length - 1
              ] = this.scheduleDFinalData[x].Line_data[j + 1].Line_data_occ[0];
              this.scheduleDFinalData[x].Line_data.splice(j + 1, 1);
            }
            for (let k = 0; k < this.scheduleDFinalData[x].Line_data[j].Line_data_occ.length; k++) {
              finalData.push(this.scheduleDFinalData[x].Line_data[j].Line_data_occ[k]);
            }
          }
        }
        if (this.scheduleDFinalData[x].DELETE_ROWS.length != null &&
            this.scheduleDFinalData[x].DELETE_ROWS.length != undefined) {
          for (let j = 0; j < this.scheduleDFinalData[x].DELETE_ROWS.length; j++) {
            for (let k = 0; k < this.scheduleDFinalData[x].DELETE_ROWS[j].Line_data_occ.length; k++) {
              this.scheduleDFinalData[x].DELETE_ROWS[j].Line_data_occ[k].IS_CHANGED_FLAG = "D";
              finalData.push(this.scheduleDFinalData[x].DELETE_ROWS[j].Line_data_occ[k]);
            }
          }
        }
      }
      if (this.scheduleDFinalData[x].ROW_TYPE == 'TH' || this.scheduleDFinalData[x].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.scheduleDFinalData[x].Line_data.length; j++) {
          finalData.push(this.scheduleDFinalData[x].Line_data[j]);
        }
      }
    }

    const clob_settings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.globalParams?.screen_key
    }];

    this.fSchddService.saveSchddDetails(
      this.GENERAL_CONFIG.base_url,
      finalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Schedule D Details are successfully updated.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.loadFormData();
  }

  cancel() {
    if (_.isEqual(this.scheduleDFormData, this.scheduleDOriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open('The transaction is still in progress', 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
        this.requestGridUpdate.emit('cancel');
      } else if (confirm("Capital Gain or (Loss) Information is not save. Do you want to close Form without saving changes?")) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  updateFormDataValues() {
    for (let i = 0; i < this.scheduleDFinalData.length; i++) {
      if (this.scheduleDFinalData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.scheduleDFinalData[i].Line_data.length; j++) {
          if (this.scheduleDFinalData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            for (let l = 0; l < this.scheduleDFinalData[i].Line_data[j].Line_data_occ.length; l++) {
              const item = this.scheduleDFinalData[i].Line_data[j].Line_data_occ[l];
              if (item.IS_EDITABLE_CELL == "Y" &&
                  item.DATA_TYPE == "dateVarious" &&
                  item.EDIT_TYPE == "custom_date_various") {
                item.ATTRIB_VALUE = this.getDateFormat(item.ATTRIB_VALUE);
              } else if (item.IS_EDITABLE_CELL == "Y" &&
                         item.DATA_TYPE == "string" &&
                         item.EDIT_TYPE == "radio") {
                if (item.ATTRIB_VALUE == null || item.ATTRIB_VALUE == undefined) {
                  item.ATTRIB_VALUE = "Temp";
                }
              } else if (item.IS_EDITABLE_CELL == "Y" &&
                         item.DATA_TYPE == "string" &&
                         item.EDIT_TYPE == "checkbox") {
                if (item.ATTRIB_VALUE == null || item.ATTRIB_VALUE == undefined) {
                  item.ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
  }

  getDateFormat(date_value: any): string {
    if (date_value == "VARIOUS") {
      return "VARIOUS";
    }

    // Handle YYYY-MM-DD format (from HTML5 date input)
    if (typeof date_value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date_value)) {
      const parts = date_value.split('-');
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }

    if (date_value instanceof Date) {
      const month = (date_value.getMonth() > 8) ?
        (date_value.getMonth() + 1).toString() :
        '0' + (date_value.getMonth() + 1);
      const day = (date_value.getDate() > 9) ?
        date_value.getDate().toString() :
        '0' + date_value.getDate();
      return month + '/' + day + '/' + date_value.getFullYear();
    }

    // If already in MM/DD/YYYY format, return as is
    return date_value;
  }

  // Convert date to YYYY-MM-DD format for HTML date input
  convertToHtmlDateFormat(date_value: any): string {
    if (date_value == "VARIOUS" || !date_value) {
      return "";
    }

    let dateObj: Date;

    // If it's already a Date object, use it
    if (date_value instanceof Date) {
      dateObj = date_value;
    } else {
      // Try to parse the date string (handles MM/DD/YYYY format)
      if (typeof date_value === 'string' && date_value.includes('/')) {
        const parts = date_value.split('/');
        if (parts.length === 3) {
          dateObj = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
        } else {
          dateObj = new Date(date_value);
        }
      } else {
        dateObj = new Date(date_value);
      }
    }

    // Check if valid date
    if (isNaN(dateObj.getTime())) {
      return "";
    }

    // Format as YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getDateValueForInput(date_value: any): string {
    if (!date_value || date_value === 'VARIOUS') {
      return '';
    }
    // If already in YYYY-MM-DD format, return as-is
    if (typeof date_value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date_value)) {
      return date_value;
    }
    // Otherwise, convert it
    return this.convertToHtmlDateFormat(date_value);
  }

  // Handle date various checkbox change
  onDateVariousCheckboxChange(column: any, mainIndex: number, occurrence: any, lineNo: string, colIndex: number, rowType: string, event: any) {
    const isChecked = event.target.checked;

    if (isChecked) {
      // Set to "VARIOUS" when checkbox is checked
      column.ATTRIB_VALUE = 'VARIOUS';
    } else {
      // Set to current date in YYYY-MM-DD format when checkbox is unchecked
      column.ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
    }

    this.valuechanged(mainIndex, occurrence, lineNo, colIndex, rowType);
  }

  validateFormValues(): number {
    let returnValue = 0;
    const tax_year = parseInt(this.globalService?.globalParams?.tax_year || this.rowData.TAX_YEAR);

    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
          const line_no = this.scheduleDFormData[i].Line_data[j].Line_no;
          let part_no = "I";
          if (line_no == "10" || line_no == "9" || line_no == "8b") {
            part_no = "II";
          }

          if (this.scheduleDFormData[i].Line_data[j].Line_Type == "DATA_ROW" ||
              this.scheduleDFormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            const lineDataOcc = this.scheduleDFormData[i].Line_data[j].Line_data_occ;

            const property_desc_index = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "PROPERTY_DESC_" + line_no);
            const date_acquired_index = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "DATE_ACQ_" + line_no);
            const date_sold_index = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "DATE_SOLD_" + line_no);
            const sales_price_index = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "SALE_PRICE_" + line_no);
            const cost_basis_index = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "COST_BASIS_" + line_no);
            const code_instructions = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "ADJ_AMT_CODE_" + line_no);
            const amt_adjusted = lineDataOcc.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "ADJS_AMT_" + line_no);

            // Validate property description
            if (!lineDataOcc[property_desc_index].ATTRIB_VALUE ||
                lineDataOcc[property_desc_index].ATTRIB_VALUE.length <= 0) {
              lineDataOcc[property_desc_index].HAS_ERROR = "Y";
              lineDataOcc[property_desc_index].ATTRIB_VALUE_ERROR = "Enter Description of Property.";
              returnValue = 1;
            }

            // Validate dates and amounts
            // EXACT AngularJS: Skip validation if ALL conditions are true (code == "Z" AND date_sold == "VARIOUS" AND sales_price == 0 AND cost_basis == 0)
            if (!(lineDataOcc[code_instructions].ATTRIB_VALUE == "Z" &&
                  lineDataOcc[date_sold_index].ATTRIB_VALUE == "VARIOUS" &&
                  lineDataOcc[sales_price_index].ATTRIB_VALUE == 0 &&
                  lineDataOcc[cost_basis_index].ATTRIB_VALUE == 0)) {

              // Validate date acquired - EXACT AngularJS logic
              if (lineDataOcc[date_acquired_index].ATTRIB_VALUE != "VARIOUS") {
                const dateAcquiredValue = lineDataOcc[date_acquired_index].ATTRIB_VALUE;
                // AngularJS checks if it's an object (Date) or string
                if (typeof dateAcquiredValue === 'object' && dateAcquiredValue instanceof Date) {
                  // AngularJS uses getYear() which returns year - 1900
                  if (dateAcquiredValue.getFullYear() > tax_year) {
                    lineDataOcc[date_acquired_index].HAS_ERROR = "Y";
                    lineDataOcc[date_acquired_index].ATTRIB_VALUE_ERROR =
                      "The Date Acquired year has to be current tax year or prior year.";
                    returnValue = 1;
                  }
                } else if (typeof dateAcquiredValue === 'string') {
                  // AngularJS extracts year from string using regex match
                  const yearMatch = dateAcquiredValue.match(/\d{4}$/);
                  if (yearMatch && parseInt(yearMatch[0]) > tax_year) {
                    lineDataOcc[date_acquired_index].HAS_ERROR = "Y";
                    lineDataOcc[date_acquired_index].ATTRIB_VALUE_ERROR =
                      "The Date Acquired year has to be current tax year or prior year.";
                    returnValue = 1;
                  }
                }
              }

              // Validate date sold - EXACT AngularJS logic
              if (lineDataOcc[date_sold_index].ATTRIB_VALUE != "VARIOUS") {
                const dateSoldValue = lineDataOcc[date_sold_index].ATTRIB_VALUE;
                // AngularJS checks if it's an object (Date) or string
                if (typeof dateSoldValue === 'object' && dateSoldValue instanceof Date) {
                  // AngularJS uses getYear() which returns year - 1900
                  if (dateSoldValue.getFullYear() > tax_year) {
                    lineDataOcc[date_sold_index].HAS_ERROR = "Y";
                    lineDataOcc[date_sold_index].ATTRIB_VALUE_ERROR =
                      "The Date Sold year has to be same as tax year.";
                    returnValue = 1;
                  }
                } else if (typeof dateSoldValue === 'string') {
                  // AngularJS extracts year from string using regex match
                  const yearMatch = dateSoldValue.match(/\d{4}$/);
                  if (yearMatch && parseInt(yearMatch[0]) > tax_year) {
                    lineDataOcc[date_sold_index].HAS_ERROR = "Y";
                    lineDataOcc[date_sold_index].ATTRIB_VALUE_ERROR =
                      "The Date Sold year has to be same as tax year.";
                    returnValue = 1;
                  }
                }
              }

              // Validate date format mismatch - Date Acquired and Date Sold must be in the same format
              // One cannot be "VARIOUS" while the other is an actual date
              const dateAcqIsVarious = lineDataOcc[date_acquired_index].ATTRIB_VALUE == "VARIOUS";
              const dateSoldIsVarious = lineDataOcc[date_sold_index].ATTRIB_VALUE == "VARIOUS";

              if (dateAcqIsVarious && !dateSoldIsVarious) {
                lineDataOcc[date_acquired_index].HAS_ERROR = "Y";
                lineDataOcc[date_acquired_index].ATTRIB_VALUE_ERROR =
                  "Date Acquired and Date Sold must be in the same format. Both should be dates or both should be VARIOUS.";
                returnValue = 1;
              } else if (!dateAcqIsVarious && dateSoldIsVarious) {
                lineDataOcc[date_sold_index].HAS_ERROR = "Y";
                lineDataOcc[date_sold_index].ATTRIB_VALUE_ERROR =
                  "Date Acquired and Date Sold must be in the same format. Both should be dates or both should be VARIOUS.";
                returnValue = 1;
              }

              // Validate date relationship - EXACT AngularJS logic
              if (lineDataOcc[date_sold_index].ATTRIB_VALUE != "VARIOUS" &&
                  lineDataOcc[date_acquired_index].ATTRIB_VALUE != "VARIOUS") {
                // AngularJS uses Date.parse() which handles both Date objects and strings
                const dateAcquiredParsed = Date.parse(lineDataOcc[date_acquired_index].ATTRIB_VALUE);
                const dateSoldParsed = Date.parse(lineDataOcc[date_sold_index].ATTRIB_VALUE);

                if (!isNaN(dateAcquiredParsed) && !isNaN(dateSoldParsed) && dateAcquiredParsed > dateSoldParsed) {
                  lineDataOcc[date_acquired_index].HAS_ERROR = "Y";
                  lineDataOcc[date_acquired_index].ATTRIB_VALUE_ERROR =
                    "The Date Acquired should be prior to Date Sold";
                  returnValue = 1;
                }

                // Long/short term classification check only applies when the user has actually
                // changed one of the date fields in the current edit session.
                // editExistingRecord only reformats dates (MM/DD/YYYY → YYYY-MM-DD) without
                // setting IS_CHANGED_FLAG, so rows where the user merely clicked the pencil
                // icon without touching the dates are correctly excluded. AddNewRow sets
                // IS_CHANGED_FLAG = "Y" on all fields, so new rows are always checked.
                const dateAcqChanged = lineDataOcc[date_acquired_index].IS_CHANGED_FLAG === "Y";
                const dateSoldChanged = lineDataOcc[date_sold_index].IS_CHANGED_FLAG === "Y";

                if (dateAcqChanged || dateSoldChanged) {
                  const dateAcquired = lineDataOcc[date_acquired_index].ATTRIB_VALUE;
                  const dateSold = lineDataOcc[date_sold_index].ATTRIB_VALUE;
                  const diff_in_dates = this.getDiffDates(dateAcquired, dateSold);

                  if (!isNaN(diff_in_dates)) {
                    if (diff_in_dates > 365 && part_no == "I") {
                      lineDataOcc[date_sold_index].HAS_ERROR = "Y";
                      lineDataOcc[date_sold_index].ATTRIB_VALUE_ERROR =
                        "Please enter the long Term Gain/Loss values in Part II.";
                      returnValue = 1;
                    } else if (diff_in_dates <= 365 && part_no == "II") {
                      lineDataOcc[date_sold_index].HAS_ERROR = "Y";
                      lineDataOcc[date_sold_index].ATTRIB_VALUE_ERROR =
                        "Please enter the short Term Gain/Loss values in Part I.";
                      returnValue = 1;
                    }
                  }
                }
              }

              // Validate cost basis - EXACT AngularJS logic
              if (lineDataOcc[cost_basis_index].ATTRIB_VALUE == 0) {
                const nextRow = this.scheduleDFormData[i].Line_data[j + 1];
                const commentValue = nextRow && nextRow.Line_data_occ && nextRow.Line_data_occ[0] &&
                  nextRow.Line_data_occ[0].hasOwnProperty("ATTRIB_VALUE")
                    ? nextRow.Line_data_occ[0].ATTRIB_VALUE
                    : null;
                if (commentValue == null || commentValue == undefined ||
                    (typeof commentValue === 'string' && commentValue.trim() === '')) {
                  lineDataOcc[cost_basis_index].HAS_ERROR = "Y";
                  lineDataOcc[cost_basis_index].ATTRIB_VALUE_ERROR =
                    "When Cost or Other Basis amount is 0, an explanation comment must be entered!";
                  returnValue = 1;
                }
              }
              if (lineDataOcc[cost_basis_index].ATTRIB_VALUE < 0) {
                lineDataOcc[cost_basis_index].HAS_ERROR = "Y";
                lineDataOcc[cost_basis_index].ATTRIB_VALUE_ERROR =
                  "As the Amounts are in Increase/Decrease format, Cost Basis should not be less than zero!";
                returnValue = 1;
              }

              // Validate adjustment code - EXACT AngularJS logic
              if (lineDataOcc[amt_adjusted].ATTRIB_VALUE != 0) {
                // AngularJS checks null explicitly and uses hardcoded array
                const validCodes = ['B', 'T', 'N', 'H', 'D', 'Q', 'X', 'R', 'W', 'L', 'E', 'S', 'C', 'M', 'O', 'Z', 'Y'];
                if (lineDataOcc[code_instructions].ATTRIB_VALUE == null ||
                    validCodes.indexOf(lineDataOcc[code_instructions].ATTRIB_VALUE) < 0) {
                  lineDataOcc[code_instructions].HAS_ERROR = "Y";
                  lineDataOcc[code_instructions].ATTRIB_VALUE_ERROR =
                    "Please select the Code from following list 'B', 'T', 'N', 'H', 'D', 'Q', 'X', 'R', 'W', 'L', 'E', 'S', 'C', 'M', 'O', 'Z', 'Y'";
                  returnValue = 1;
                }
              }
            }
          }
        }
      }
    }

    this.cdr.detectChanges();
    return returnValue;
  }

  parseDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string') {
      const dateMatch = dateValue.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (dateMatch) {
        return new Date(parseInt(dateMatch[3]), parseInt(dateMatch[1]) - 1, parseInt(dateMatch[2]));
      }
    }
    return null;
  }

  getDiffDates(dateAcquired: any, dateSold: any): number {
    let dateAcquiredObj: Date;
    let dateSoldObj: Date;

    const parseStringDate = (val: string): Date => {
      // YYYY-MM-DD format (HTML date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const parts = val.split('-');
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
      // MM/DD/YYYY format (server / database format, AngularJS legacy)
      const dateArr = val.split('/');
      return new Date(parseInt(dateArr[2]), parseInt(dateArr[0]) - 1, parseInt(dateArr[1]));
    };

    if (typeof dateAcquired === 'string') {
      dateAcquiredObj = parseStringDate(dateAcquired);
    } else {
      dateAcquiredObj = dateAcquired instanceof Date ? dateAcquired : new Date(dateAcquired);
    }

    if (typeof dateSold === 'string') {
      dateSoldObj = parseStringDate(dateSold);
    } else {
      dateSoldObj = dateSold instanceof Date ? dateSold : new Date(dateSold);
    }

    return Math.floor((dateSoldObj.getTime() - dateAcquiredObj.getTime()) / (1000 * 3600 * 24));
  }

  // Additional methods for row operations
  AddNewRow(line_no: string) {
    let occurrence = 1;
    let trans_details_key: string | null = null;

    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        const lastItem = this.scheduleDFormData[i].Line_data[this.scheduleDFormData[i].Line_data.length - 1];
        if (lastItem && lastItem.Occurrence != null && lastItem.Occurrence != "" && lastItem.Occurrence != undefined) {
          occurrence = parseInt(lastItem.Occurrence) + 1;
          trans_details_key = lastItem.Trans_details_key;
        }

        const new_row = _.cloneDeep(this.scheduleDFormData[i].Line_data[0]);
        new_row.Line_Type = "NEW_EDIT_ROW";
        new_row.Occurrence = occurrence.toString();
        new_row.Trans_details_key = trans_details_key;
        new_row.IS_EDITTED = "Y";

        if (new_row.Line_no == line_no) {
          for (let j = 0; j < new_row.Line_data_occ.length; j++) {
            if (new_row.Line_data_occ[j].EDIT_TYPE == "custom_date_various" &&
                new_row.Line_data_occ[j].DATA_TYPE == "dateVarious") {
              // Convert current date to YYYY-MM-DD format for HTML date input
              new_row.Line_data_occ[j].ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "currency" &&
                       new_row.Line_data_occ[j].DATA_TYPE == "number") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = 0;
            } else if (new_row.Line_data_occ[j].EDIT_TYPE != "label" ||
                       new_row.Line_data_occ[j].DATA_TYPE != "string") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = null;
            }
            new_row.Line_data_occ[j].OCCURRENCE = occurrence;
            new_row.Line_data_occ[j].TRANS_DETAILS_KEY = trans_details_key;
            new_row.Line_data_occ[j].IS_CHANGED_FLAG = "Y";
          }
          this.scheduleDFormData[i].Line_data.push(new_row);
        }

        const new_row_comments = _.cloneDeep(this.scheduleDFormData[i].Line_data[0]);
        new_row_comments.Line_Type = "NEW_EDIT_COMMENT_ROW";
        new_row_comments.Occurrence = occurrence.toString();
        new_row_comments.Trans_details_key = trans_details_key;
        new_row_comments.IS_EDITTED = "Y";

        if (new_row_comments.Line_no == line_no) {
          new_row_comments.Line_data_occ = [new_row_comments.Line_data_occ[new_row_comments.Line_data_occ.length - 1]];
          new_row_comments.Line_data_occ[0].OCCURRENCE = occurrence;
          new_row_comments.Line_data_occ[0].ATTRIB_VALUE = null;
          new_row_comments.Line_data_occ[0].TRANS_DETAILS_KEY = trans_details_key;
          new_row_comments.Line_data_occ[0].IS_CHANGED_FLAG = "Y";
          this.scheduleDFormData[i].Line_data.push(new_row_comments);
        }
      }
    }
    this.cdr.detectChanges();
  }

  editExistingRecord(line_no: string, occurrence: string, index: number) {
    // EXACT match to AngularJS version - line by line translation
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            const item = this.scheduleDFormData[i].Line_data[j];

            // EXACT AngularJS: if (vm.scheduleDFormData[i].Line_data[j].Line_Type == "DATA_ROW" && ...)
            if (item.Line_Type == "DATA_ROW" &&
                item.Line_no == line_no &&
                item.Occurrence == occurrence) {

              // EXACT AngularJS: vm.scheduleDFormData[i].Line_data[j].Line_Type = "NEW_EDIT_ROW";
              item.Line_Type = "NEW_EDIT_ROW";

              // EXACT AngularJS: for(var l=0; l < vm.scheduleDFormData[i].Line_data[j].Line_data_occ.length; l++)
              for (let l = 0; l < item.Line_data_occ.length; l++) {
                const column = item.Line_data_occ[l];

                // EXACT AngularJS: if (vm.scheduleDFormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "currency" && ...)
                if (column.EDIT_TYPE == "currency" && column.DATA_TYPE == "number") {
                  // EXACT AngularJS: vm.scheduleDFormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = parseInt(...)
                  column.ATTRIB_VALUE = parseInt(column.ATTRIB_VALUE);
                }
                // EXACT AngularJS: else if (vm.scheduleDFormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various" && ...)
                else if (column.EDIT_TYPE == "custom_date_various" && column.DATA_TYPE == "dateVarious") {
                  // EXACT AngularJS: if (vm.scheduleDFormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE != "VARIOUS")
                  if (column.ATTRIB_VALUE != "VARIOUS") {
                    // AngularJS used: new Date(vm.scheduleDFormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE)
                    // But HTML date input needs YYYY-MM-DD string format
                    column.ATTRIB_VALUE = this.convertToHtmlDateFormat(column.ATTRIB_VALUE);
                  }
                }
                // Trim select values to ensure proper matching with dropdown options
                else if (column.EDIT_TYPE == "select" && column.DATA_TYPE == "string") {
                  if (column.ATTRIB_VALUE != null && typeof column.ATTRIB_VALUE === 'string') {
                    column.ATTRIB_VALUE = column.ATTRIB_VALUE.trim();
                  }
                }
              }
            }
            // EXACT AngularJS: else if (vm.scheduleDFormData[i].Line_data[j].Line_Type == "DATA_COMMENTS" && ...)
            else if (item.Line_Type == "DATA_COMMENTS" &&
                     item.Line_no == line_no &&
                     item.Occurrence == occurrence) {
              // EXACT AngularJS: vm.scheduleDFormData[i].Line_data[j].Line_Type = "NEW_EDIT_COMMENT_ROW";
              item.Line_Type = "NEW_EDIT_COMMENT_ROW";
            }
          }
        }
      }
    }

    // Create new array references to trigger OnPush change detection
    // (AngularJS doesn't need this due to dirty checking, but Angular OnPush requires it)
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        this.scheduleDFormData[i].Line_data = [...this.scheduleDFormData[i].Line_data];
      }
    }
    this.scheduleDFormData = [...this.scheduleDFormData];

    // Force change detection for OnPush strategy - run in Angular zone
    this.ngZone.run(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      // Use requestAnimationFrame to ensure DOM is fully created before ngModel tries to bind
      // This is critical for *ngIf scenarios where DOM elements are created dynamically
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          // Force another change detection after DOM is ready
          this.cdr.detectChanges();

          // One more check after a microtask to ensure ngModel is initialized
          setTimeout(() => {
            this.ngZone.run(() => {
              this.cdr.detectChanges();
            });
          }, 0);
        });
      });
    });
  }

  removeExistingRecord(line_no: string, occurrence: string, index: number) {
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
          if (j == index) {
            const item = this.scheduleDFormData[i].Line_data[j];
            if ((item.Line_Type == "DATA_ROW" || item.Line_Type == "NEW_EDIT_ROW") &&
                item.Line_no == line_no &&
                item.Occurrence != null &&
                item.Occurrence != "" &&
                item.Occurrence == occurrence) {
              this.scheduleDFormData[i].DELETE_ROWS.push(item);
              this.scheduleDFormData[i].Line_data.splice(j, 2);
            } else if ((item.Line_Type == "DATA_ROW" || item.Line_Type == "NEW_EDIT_ROW") &&
                       item.Line_no == line_no &&
                       (occurrence == null || occurrence == "")) {
              this.scheduleDFormData[i].Line_data.splice(j, 2);
            }
          }
        }
      }
    }
    this.calcFinalAmounts();
    this.cdr.detectChanges();
  }

  saveNewEdittedRecord(line_no: string, occurrence: string, index: number) {
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            const item = this.scheduleDFormData[i].Line_data[j];
            if (item.Line_Type == "NEW_EDIT_ROW" &&
                item.Line_no == line_no &&
                item.Occurrence == occurrence) {
              // Change back to DATA_ROW - values are already saved in ATTRIB_VALUE via ngModel
              item.Line_Type = "DATA_ROW";
            } else if (item.Line_Type == "NEW_EDIT_COMMENT_ROW" &&
                       item.Line_no == line_no &&
                       item.Occurrence == occurrence) {
              // Change back to DATA_COMMENTS - comment value is already saved via ngModel
              item.Line_Type = "DATA_COMMENTS";
            }
          }
          // Process and format the values before saving (like AngularJS)
          if (j == index &&
              this.scheduleDFormData[i].Line_data[j].Line_no == line_no &&
              this.scheduleDFormData[i].Line_data[j].Occurrence == occurrence) {
            for (let l = 0; l < this.scheduleDFormData[i].Line_data[j].Line_data_occ.length; l++) {
              const column = this.scheduleDFormData[i].Line_data[j].Line_data_occ[l];
              // Format date values back to database format
              if (column.IS_EDITABLE_CELL == "Y" &&
                  column.DATA_TYPE == "dateVarious" &&
                  column.EDIT_TYPE == "custom_date_various") {
                column.ATTRIB_VALUE = this.getDateFormat(column.ATTRIB_VALUE);
              }
              // Set default for radio if null
              else if (column.IS_EDITABLE_CELL == "Y" &&
                       column.DATA_TYPE == "string" &&
                       column.EDIT_TYPE == "radio") {
                if (column.ATTRIB_VALUE == null || column.ATTRIB_VALUE == undefined) {
                  column.ATTRIB_VALUE = "Temp";
                }
              }
              // Set default for checkbox if null
              else if (column.IS_EDITABLE_CELL == "Y" &&
                       column.DATA_TYPE == "string" &&
                       column.EDIT_TYPE == "checkbox") {
                if (column.ATTRIB_VALUE == null || column.ATTRIB_VALUE == undefined) {
                  column.ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
        // Create new array references to trigger OnPush change detection
        this.scheduleDFormData[i].Line_data = [...this.scheduleDFormData[i].Line_data];
      }
    }
    // Create new reference for parent array
    this.scheduleDFormData = [...this.scheduleDFormData];

    // Recalculate final amounts after saving
    this.calcFinalAmounts();

    // Force change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  valuechanged(parent_index: number, occurrence: string, line_no: string, index: number, row_type: string) {
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH' && this.scheduleDFormData[i].ROW_TYPE == row_type) {
        // Search through Line_data by line_no + occurrence instead of using parent_index as an array index
        for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
          const item = this.scheduleDFormData[i].Line_data[j];
          if (item && item.Line_no == line_no && item.Occurrence == occurrence) {
            if (item.Line_Type == "NEW_EDIT_COMMENT_ROW") {
              item.Line_data_occ[0].IS_CHANGED_FLAG = "Y";
            } else {
              if (item.Line_data_occ[index + 1]) {
                item.Line_data_occ[index + 1].IS_CHANGED_FLAG = "Y";
                item.Line_data_occ[index + 1].HAS_ERROR = "N";
              }
            }
          }
        }
      } else if (this.scheduleDFormData[i].ROW_TYPE == 'DT' && this.scheduleDFormData[i].ROW_TYPE == row_type) {
        if (i == parent_index && this.scheduleDFormData[i].Line_data[index].LINE_NO == line_no) {
          this.scheduleDFormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
        }
      }
    }
    this.cdr.detectChanges();
  }

  updateAmount(index: number, line_no: string) {
    for (let i = 0; i < this.scheduleDFormData.length; i++) {
      if (this.scheduleDFormData[i].ROW_TYPE == 'CH' &&
          this.scheduleDFormData[i].Line_data[0].Line_no == line_no) {
        for (let j = 0; j < this.scheduleDFormData[i].Line_data[index].Line_data_occ.length; j++) {
          const item = this.scheduleDFormData[i].Line_data[index].Line_data_occ[j];
          if (item.ATTRIB_CALC_FORMULA != null) {
            const line_data_values = this.scheduleDFormData[i].Line_data[index].Line_data_occ;
            item.ATTRIB_VALUE = this.calcLineFormula(item.ATTRIB_CALC_FORMULA, line_data_values);
            item.IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.calcFinalAmounts();
    this.cdr.detectChanges();
  }

  calcLineFormula(formula: string, line_data_values: any[]): number {
    const lineFormulaArray = formula.split(/[+\-/*]/);
    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        const code = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "").trim();
        const currentTabAtrribVal = line_data_values.filter((x: any) => x.ATTRIB_SHORT_CODE == code);
        if (currentTabAtrribVal.length > 0) {
          const lineAmt = parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
          if (parseInt(lineAmt.toString()) > 0) {
            formula = formula.replace("(" + code + ")", lineAmt.toString());
          } else {
            formula = formula.replace("(" + code + ")", "(" + lineAmt.toString() + ")");
          }
        }
      }
    }

    try {
      const result = safeEvalFormula(formula, null, 'f-schdd');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error calculating formula:', e);
    }
    return 0;
  }

  calcFinalAmounts() {
    // Two-pass: first pass computes TH aggregates from CH/DT rows (e.g. GAIN_LOSS_FORM),
    // second pass computes TH values that reference other TH values (e.g. Unallocated Amount
    // which depends on GAIN_LOSS_TI and GAIN_LOSS_FORM).
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < this.scheduleDFormData.length; i++) {
        if (this.scheduleDFormData[i].ROW_TYPE == 'TH') {
          for (let j = 0; j < this.scheduleDFormData[i].Line_data.length; j++) {
            const item = this.scheduleDFormData[i].Line_data[j];
            if (item.ATTRIB_CALC_FORMULA != null) {
              item.ATTRIB_VALUE = this.calc_final_amt(item.ATTRIB_CALC_FORMULA, item.ATTRIB_SHORT_CODE);
              item.IS_CHANGED_FLAG = "Y";
            }
          }
        }
      }
    }
    this.getUnallocatedAmt();
    this.cdr.detectChanges();
  }

  calc_final_amt(formula: string, selfShortCode?: string): number {
    const lineFormulaArray = formula.split(/[+\-/*]/);
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt = 0;
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        const code = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "").trim();
        for (let k = 0; k < this.scheduleDFormData.length; k++) {
          if (this.scheduleDFormData[k].ROW_TYPE == 'CH') {
            for (let j = 0; j < this.scheduleDFormData[k].Line_data.length; j++) {
              if (this.scheduleDFormData[k].Line_data[j].Line_Type == "DATA_ROW" ||
                  this.scheduleDFormData[k].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
                const row_data = this.scheduleDFormData[k].Line_data[j].Line_data_occ;
                const currentTabAtrribVal = row_data.filter((x: any) => x.ATTRIB_SHORT_CODE == code);
                if (currentTabAtrribVal.length > 0 && currentTabAtrribVal[0].ATTRIB_VALUE != null && currentTabAtrribVal[0].ATTRIB_VALUE !== undefined) {
                  const value = parseInt(currentTabAtrribVal[0].ATTRIB_VALUE) || 0;
                  lineAmt = lineAmt + value;
                }
              }
            }
          } else {
            // Handle both 'DT' and 'TH' rows (parity with AngularJS behavior).
            // Formulas like Unallocated Amount's `(GAIN_LOSS_TI)-(GAIN_LOSS_FORM)`
            // reference attributes that live on TH rows, so skipping them caused
            // the result to collapse to 0.
            const row_data = this.scheduleDFormData[k].Line_data;
            const currentTabAtrribVal = row_data.filter((x: any) => x.ATTRIB_SHORT_CODE == code);
            if (currentTabAtrribVal.length > 0 && currentTabAtrribVal[0].ATTRIB_VALUE != null && currentTabAtrribVal[0].ATTRIB_VALUE !== undefined) {
              const value = parseInt(currentTabAtrribVal[0].ATTRIB_VALUE) || 0;
              lineAmt = lineAmt + value;
            }
          }
        }
        if (parseInt(lineAmt.toString()) > 0) {
          formula = formula.replace("(" + code + ")", lineAmt.toString());
        } else {
          formula = formula.replace("(" + code + ")", "(" + lineAmt.toString() + ")");
        }
      }
    }

    try {
      const result = safeEvalFormula(formula, null, 'f-schdd');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error calculating final amount:', e);
    }
    return 0;
  }

  // Radio button handler for Yes/No inputs
  onRadioChange(index: number, value: string, row: any) {
    row.ATTRIB_VALUE = value;
    row.IS_CHANGED_FLAG = 'Y';
    this.cdr.detectChanges();
  }

  // Number formatting helper
  numberWithCommas(value: any): string {
    if (value == null || value == undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value.toString();
    return num.toLocaleString('en-US');
  }

  // Helper method for category code dropdown
  getCategoryCodeKeys(): string[] {
    return Object.keys(this.categoryCode);
  }

  // Helper: get dropdown options for "Checkbox on 8949" by line number
  getCheckbox8949Options(lineNo: string): string[] {
    return this.checkbox8949OptionsMap[lineNo] || [];
  }

  // Loss transaction handler - opens the Angular Reportable Transaction modal
  lossTransaction(attrib_short_code: string, mainIndex: number, parentIndex: number, attrib_value: string, occurrence: string) {
    console.log('lossTransaction called:', { attrib_short_code, mainIndex, parentIndex, attrib_value, occurrence });

    // Parse the attrib_short_code to get short_desc and line_no
    const short_desc = attrib_short_code ? attrib_short_code.substring(0, attrib_short_code.lastIndexOf("_")) : '';
    const line_no = attrib_short_code ? attrib_short_code.substring(attrib_short_code.lastIndexOf("_") + 1) : '';

    // Check if this is a loss transaction that should open the modal
    if (short_desc === "LOSS_TRANSACTION" && (attrib_value === "Y" || attrib_value === "YES")) {
      // Get the data row for this transaction
      const dataRow = this.scheduleDFormData[mainIndex]?.Line_data?.[parentIndex];

      if (!dataRow) {
        console.error('Could not find dataRow');
        return;
      }

      console.log('DataRow structure:', {
        Line_no: dataRow.Line_no,
        Occurrence: dataRow.Occurrence,
        Trans_details_key: dataRow.Trans_details_key,
        Line_data_occ_length: dataRow.Line_data_occ?.length
      });

      // Standalone Angular 20 app: always open the dialog directly via MatDialog.
      // The legacy AngularJS bridge path has been removed.
      this.openReportableTransactionModal(dataRow);
    }
  }

  // Open the Reportable Transaction Angular component as a modal dialog
  openReportableTransactionModal(dataRow: any): void {
    console.log('Opening Reportable Transaction Modal with data:');
    console.log('dataRow:', dataRow);
    console.log('rowData:', this.rowData);
    console.log('filterParams:', this.filterParams);
    console.log('USER_SETTINGS:', this.appService?.USER_SETTINGS);
    console.log('isValidScenario being passed:', this.isValidScenario);

    // Open the dialog with high z-index to appear on top of existing f-schdd modal
    // disableClose: true prevents closing on backdrop click - user must use Cancel button
    this.reportableTransactionDialogRef = this.dialog.open(ReportableTransactionComponent, {
      width: '90%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      disableClose: true, // Prevent closing on backdrop click
      panelClass: ['reportable-transaction-dialog', 'reportable-transaction-dialog-pane'],
      backdropClass: 'reportable-transaction-dialog-backdrop',
      hasBackdrop: true,
      autoFocus: true,
      data: {
        dataRow,
        rowData: this.rowData,
        repTransaction: [],
        isValidScenario: this.isValidScenario,
      },
    });

    const componentInstance = this.reportableTransactionDialogRef.componentInstance;
    this.subs.add(
      componentInstance.requestClose.subscribe(() => {
        this.reportableTransactionDialogRef?.close();
      })
    );

    this.subs.add(
      this.reportableTransactionDialogRef.afterClosed().subscribe((result: any) => {
        console.log('Reportable Transaction modal closed', result);
        this.reportableTransactionDialogRef = null;
        this.cdr.markForCheck();
      })
    );
  }

  // Verify loss transaction when checkbox is unchecked
  verifyLossTransaction(attrib_short_code: string, mainIndex: number, parentIndex: number, attrib_value: string, occurrence: number, trans_details_key: string) {
    const short_desc = attrib_short_code.substring(0, attrib_short_code.lastIndexOf("_"));
    const line_no = attrib_short_code.substring(attrib_short_code.lastIndexOf("_") + 1, attrib_short_code.length);

    if (short_desc != null && short_desc == "LOSS_TRANSACTION" && (attrib_value == "N" || attrib_value == "NO")) {
      const success_message = "Reportable Transaction was successfully removed!!";
      const message = "Please save the transaction to update the Loss Transaction Column";

      // Call service to delete reportable transaction
      // Note: This service method needs to be implemented in f-schdd.service.ts
      // this.fSchddService.deleteRepTransaction(...).subscribe(...);
      console.log('Verify loss transaction:', { short_desc, line_no, occurrence, trans_details_key });
    }
  }

  // Checkbox change handler
  onCheckboxChange(event: Event, column: any, mainIndex: number, colIndex: number, itemIndex: number) {
    const target = event.target as HTMLInputElement;
    const oldValue = column.ATTRIB_VALUE;
    column.ATTRIB_VALUE = target.checked ? 'Y' : 'N';

    // Call verifyLossTransaction if this is a loss transaction checkbox and value changed to 'N'
    if (column.ATTRIB_DESCRIPTION === 'Loss Transaction?' && (oldValue === 'Y' || oldValue === 'YES') && column.ATTRIB_VALUE === 'N') {
      this.verifyLossTransaction(column.ATTRIB_SHORT_CODE, mainIndex, itemIndex, column.ATTRIB_VALUE, column.OCCURRENCE, column.TRANS_DETAILS_KEY);
    }

    this.valuechanged(mainIndex, column.OCCURRENCE, column.LINE_NO, colIndex, column.ROW_TYPE);
  }
}


